/**
 * localStorage Migration Utilities
 * 
 * Handles migration of localStorage data between versions.
 * All migrations are idempotent and can be run multiple times safely.
 * 
 * @module lib/localStorage/migration
 */

import { logger } from '@/lib/logging';

const LEGACY_CONCILIATION_BATCHES_KEY = 'conciliation-batches';
const LEGACY_BATCHES_BACKUP_KEY = 'conciliation-batches-v1-backup';
const MIGRATION_VERSION_KEY = 'conciliation-migration-version';
const CURRENT_MIGRATION_VERSION = 2;

/**
 * Check if a migration has already been run
 */
function getMigrationVersion(): number {
  const version = localStorage.getItem(MIGRATION_VERSION_KEY);
  return version ? parseInt(version, 10) : 0;
}

/**
 * Set the current migration version
 */
function setMigrationVersion(version: number): void {
  localStorage.setItem(MIGRATION_VERSION_KEY, String(version));
}

/**
 * Migrate Conciliation V1 batches to V2 format
 * 
 * This migration:
 * 1. Backs up the old conciliation-batches key
 * 2. Removes the old key to prevent conflicts with V2 store
 * 
 * Idempotent: Safe to run multiple times
 */
export function migrateConciliationBatchesV1(): { migrated: boolean; backedUp: number } {
  const currentVersion = getMigrationVersion();
  
  // Already migrated
  if (currentVersion >= 1) {
    return { migrated: false, backedUp: 0 };
  }
  
  const legacyData = localStorage.getItem(LEGACY_CONCILIATION_BATCHES_KEY);
  
  if (!legacyData) {
    // No legacy data to migrate
    setMigrationVersion(1);
    return { migrated: false, backedUp: 0 };
  }
  
  try {
    // Parse to count items
    const parsed = JSON.parse(legacyData);
    const batchCount = Array.isArray(parsed) ? parsed.length : 
                       (parsed?.state?.batches?.length ?? 1);
    
    // Backup the old data
    localStorage.setItem(LEGACY_BATCHES_BACKUP_KEY, legacyData);
    
    // Remove the old key
    localStorage.removeItem(LEGACY_CONCILIATION_BATCHES_KEY);
    
    // Mark migration complete
    setMigrationVersion(1);
    
    logger.info('MIGRATION_CONCILIATION_V1_COMPLETE', { 
      backedUp: batchCount,
      backupKey: LEGACY_BATCHES_BACKUP_KEY 
    });
    
    return { migrated: true, backedUp: batchCount };
  } catch (error) {
    logger.error('MIGRATION_CONCILIATION_V1_ERROR', { error });
    // Still remove the corrupted data
    localStorage.removeItem(LEGACY_CONCILIATION_BATCHES_KEY);
    setMigrationVersion(1);
    return { migrated: false, backedUp: 0 };
  }
}

/**
 * Run all pending migrations
 * 
 * Should be called on app startup.
 * All migrations are idempotent and safe to run multiple times.
 */
export function runAllMigrations(): void {
  const currentVersion = getMigrationVersion();
  
  if (currentVersion >= CURRENT_MIGRATION_VERSION) {
    return; // All migrations complete
  }
  
  logger.info('MIGRATIONS_STARTING', { 
    currentVersion, 
    targetVersion: CURRENT_MIGRATION_VERSION 
  });
  
  // Run V1 → V2 migration
  if (currentVersion < 1) {
    migrateConciliationBatchesV1();
  }
  
  // Set final version
  setMigrationVersion(CURRENT_MIGRATION_VERSION);
  
  logger.info('MIGRATIONS_COMPLETE', { version: CURRENT_MIGRATION_VERSION });
}

/**
 * Clear migration state (for testing only)
 */
export function resetMigrationState(): void {
  localStorage.removeItem(MIGRATION_VERSION_KEY);
}
